import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { profileService } from '../../services/profileService';
import './ProfileForm.css';

const ProfileForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    employee_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    location: '',
    years_of_experience: '',
    profile_type: 'employee',
    bio: '',
    linkedin_url: '',
    github_url: '',
    portfolio_url: ''
  });

  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      loadProfile();
    }
  }, [id]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await profileService.getProfile(id);
      const data = response.data || response;
      
      // Map backend fields to form fields
      const nameParts = (data.full_name || '').split(' ');
      setFormData({
        employee_id: data.employee_id || '',
        first_name: data.first_name || nameParts[0] || '',
        last_name: data.last_name || nameParts.slice(1).join(' ') || '',
        email: data.email || '',
        phone: data.phone || '',
        position: data.position || data.designation || '',
        department: data.department || '',
        location: data.location || '',
        years_of_experience: data.years_of_experience || '',
        profile_type: data.profile_type || 'employee',
        bio: data.bio || '',
        linkedin_url: data.linkedin_url || '',
        github_url: data.github_url || '',
        portfolio_url: data.portfolio_url || ''
      });
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setResume(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Prepare JSON data for profile
      const submitData = {
        full_name: `${formData.first_name} ${formData.last_name}`.trim(),
        designation: formData.position,
        email: formData.email,
        department: formData.department,
        years_of_experience: parseFloat(formData.years_of_experience) || 0,
        profile_type: formData.profile_type
      };
      
      // Add employee_id only for create
      if (!isEdit) {
        submitData.employee_id = formData.employee_id;
      }
      
      // Add optional fields
      if (formData.phone) submitData.phone = formData.phone;
      if (formData.location) submitData.location = formData.location;
      if (formData.bio) submitData.bio = formData.bio;
      if (formData.linkedin_url) submitData.linkedin_url = formData.linkedin_url;
      if (formData.github_url) submitData.github_url = formData.github_url;
      if (formData.portfolio_url) submitData.portfolio_url = formData.portfolio_url;

      // Create or update profile
      let profileId = id;
      if (isEdit) {
        await profileService.updateProfile(id, submitData);
      } else {
        const result = await profileService.createProfile(submitData);
        profileId = result.id || result.data?.id;
      }
      
      // Upload resume separately if provided
      if (resume && profileId) {
        const formData = new FormData();
        formData.append('resume', resume);
        await profileService.uploadResume(profileId, formData);
      }
      
      navigate('/profiles');
    } catch (err) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return <div className="loading">Loading profile...</div>;
  }

  return (
    <div className="profile-form-container">
      <div className="form-header">
        <h2>{isEdit ? 'Edit Profile' : 'Create New Profile'}</h2>
        <button onClick={() => navigate('/profiles')} className="btn-secondary">
          Cancel
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-section">
          <h3>Basic Information</h3>
          
          {!isEdit && (
            <div className="form-group">
              <label>Employee ID *</label>
              <input
                type="text"
                name="employee_id"
                value={formData.employee_id}
                onChange={handleChange}
                required
                placeholder="e.g., EMP001"
              />
            </div>
          )}
          
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Professional Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Position *</label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleChange}
                required
                placeholder="e.g., Senior Developer"
              />
            </div>
            <div className="form-group">
              <label>Years of Experience *</label>
              <input
                type="number"
                step="0.5"
                min="0"
                name="years_of_experience"
                value={formData.years_of_experience}
                onChange={handleChange}
                required
                placeholder="e.g., 5"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Profile Type *</label>
              <select
                name="profile_type"
                value={formData.profile_type}
                onChange={handleChange}
                required
              >
                <option value="employee">Employee</option>
                <option value="consultant">Consultant</option>
                <option value="external">External Candidate</option>
              </select>
            </div>
            <div className="form-group">
              <label>Department</label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Bio</label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows="4"
              placeholder="Brief professional summary..."
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Links & Resume</h3>
          <div className="form-group">
            <label>LinkedIn URL</label>
            <input
              type="url"
              name="linkedin_url"
              value={formData.linkedin_url}
              onChange={handleChange}
              placeholder="https://linkedin.com/in/..."
            />
          </div>

          <div className="form-group">
            <label>GitHub URL</label>
            <input
              type="url"
              name="github_url"
              value={formData.github_url}
              onChange={handleChange}
              placeholder="https://github.com/..."
            />
          </div>

          <div className="form-group">
            <label>Portfolio URL</label>
            <input
              type="url"
              name="portfolio_url"
              value={formData.portfolio_url}
              onChange={handleChange}
              placeholder="https://..."
            />
          </div>

          <div className="form-group">
            <label>Resume (PDF)</label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
            />
            {formData.resume_path && (
              <small className="file-info">Current: {formData.resume_path}</small>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Saving...' : (isEdit ? 'Update Profile' : 'Create Profile')}
          </button>
          <button type="button" onClick={() => navigate('/profiles')} className="btn-secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;
